/**
 * 이상한 방 - 매치 리듀서 입구 (`createMatch` · `reduceMatch` · 권한 검사 · 로비)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 4.3절(관전)
 *            `이상한방-상태모델-v0.1.md` 9.3절(CPU 이벤트는 사람과 같은 큐) · 11.3절(서버가 쥐는 것)
 *            로드맵 모델 2.2절(타입) · 2.5절(호스트가 쥐는 것 / 클라이언트가 하는 것)
 *
 * ## 순수 리듀서다 - `gameState.ts`와 같은 세 조건
 *
 * 부수효과 없음(`MatchEffect[]`로 요청만) · 시간 안 읽음(`tick{atMs}`로 들어온다) · 난수는 시드에서만.
 * 이 파일은 지금 브라우저(루프백)에서 돌지만 **서버 코드**이고, `net/matchHost.ts`가 루프백과 서버 양쪽에서
 * 같은 함수를 부른다. 그래서 `core`·`catalog`·`game` 리프만 본다(`boundary.test.ts`).
 *
 * ## 권한 검사는 여기서 끝난다
 *
 * 방장인가(`configure`·`start`·`nextRound`·`skipPhase`) · 내 작업대인가(`applyChange`류) · 관전 중인가(`shoot`·
 * `requestOrigin`·`useMagazine`) · 그 페이즈인가. **위반은 입력 상태 참조 그대로**다 - 효과도 없다. 클라이언트가
 * 보낼 수 없는 것은 `matchView`가 불리언(`canShoot` 등)으로 미리 알려 주므로, 여기까지 온 위반은 지연이나 위장이고
 * 둘 다 피드백을 줄 대상이 아니다. 예외는 "눌렀는데 안 나간" 상황(잔탄 0 · 원래 방 안 · 쿨타임)이며 그것은
 * 각 조각이 `blocked`·`originEntryDenied`로 알린다.
 *
 * ## CPU 이벤트도 여기로 들어온다
 *
 * `cpu.ts`가 만든 `PlayerEvent`는 호스트가 사람 이벤트와 **같은 큐**에 넣는다(9.3절). 이 리듀서는 누가 넣었는지
 * 모르고 알 필요도 없다 - 좌석의 `kind`는 "손으로 고칠 사람이 있는가"(`hasHumanChanger`)에만 쓰인다.
 *
 * ## 각자의 작업대는 `matchChange.ts`
 *
 * `applyChange`·`undoChange`·`commitChanges`는 M4가 붙이고 M11이 좌석마다로 늘렸다(바꾸기 페이즈만 · 자기 구획 안에서 ·
 * 예산 안에서. 동사의 집행자는 `core/verbs`, 마감 채움은 `matchPhases.finishChange`). 걸기 `raise`는 M7이 붙였다(`matchShoot.onRaise` - 술래의 것이라
 * 사격 파일에 있다). `place`·`unplace`·`predict`·`voteUnfair`는 아직 이벤트 타입만 있고 리듀서는 참조 동일로 무시한다.
 */

import { PARTY_SEATS_MIN } from '../../core/floorPlan'
import { generateBaseRoom } from '../../core/roomGen'
import type { PlayerId } from '../../core/types'
import type { Tuning } from '../tuning'
import { NO_COMBO } from '../combo'
import { NO_ORIGIN_VISIT } from '../originVisit'
import { onApplyChange, onCommitChanges, onUndoChange } from './matchChange'
import { onPlace, onUnplace } from './matchDecorate'
import { everyoneCommitted } from './matchDesk'
import { EMPTY_DOOR, onLeaveOrigin, onLeaveQueue, onRequestOrigin } from './matchOrigin'
import { LOBBY_ROUND, finishChange, onSkipVote, onTick, settleHunt, startRound } from './matchPhases'
import { idle, isHost, nextHostCandidate, patchPlayer, playerOf, roomOptionsFor, seatsInOrder } from './matchSelectors'
import { onRaise, onShoot, onSpectate, onUseMagazine } from './matchShoot'
import type {
  JoinEvent,
  MatchConfig,
  MatchDeps,
  MatchEvent,
  MatchPlayer,
  MatchState,
  MatchStep,
  MatchConfigPatch,
} from './matchTypes'

/* --- 방 규칙의 값 범위 -------------------------------------------------------------- */

/**
 * 인당 이상한 곳 개수의 규칙 범위. 하한 1은 "고칠 것이 없는 사람을 만들지 않는다"(M11 대전제 · `matchPhases.enterHunt`가
 * 던지는 "변화 0개")이고, 상한 50은 이 값의 URL 손잡이인 `?perPlayer=`(`tuning.NUMERIC_SPECS`의 `changesPerPlayer`)의
 * 상한과 **같은 값**이다 - URL로 갈 수 있는 곳까지가 규칙이 허용하는 곳이고, 그보다 큰 값은 오타이거나 위장이다.
 * 로비 스테퍼의 1~24(`ui/lobbySeats.ts`)는 이 구간 안의 손가락 범위다.
 *
 * M11에서 200을 내렸다. 이 값이 라운드 총량이 아니라 **인당 예산**이 되면서 마감 채움이 구획 하나짜리 평면도로 돌기
 * 때문이다(`matchPhases.fillZone`) - 8석 평면도의 구획 하나가 담을 수 있는 이상한 곳은 40개 남짓이고, 그보다 큰 값은
 * `changeGen.generateChanges`가 던져 그 방을 통째로 닫는다(`server/src/room.ts`의 `guard`).
 */
export const CHANGE_COUNT_MIN = 1
export const CHANGE_COUNT_MAX = 50

/**
 * 인당 배치 상한의 상한(M10 꾸미기). 지금 기본은 0이고 화면에서 만지는 손잡이가 없지만, `configure`가 열려 있는 필드라
 * 음수·거대값을 접는 자리가 필요하다 - 음수는 "배치할 수 없다"를 뜻하지 않고 비교(`>=`)를 뒤집는다.
 */
const MAX_PLACEMENTS_PER_PLAYER = 200

/**
 * 매치를 만드는 순간의 좌석 수. 방장 하나다 - `createMatch`가 임시 방을 세울 때 `planFor`에 넘기는 값이고,
 * 여럿이 평면도의 하한(2석)보다 작아 `core/partyPlan.clampPartySeats`가 접는다. 진짜 좌석 수는 `start`에서 굳는다.
 */
const LOBBY_SEAT_COUNT = 1

/* --- 생성 ------------------------------------------------------------------------ */

/**
 * 로비에 앉은 새 좌석. 라운드 자원은 0이다 - 값은 `startRound`가 채우고, 여기서 채우면 "로비에서 탄환 16발"이라는
 * 뜻 없는 상태가 화면에 잠깐 보인다. CPU는 준비 버튼이 없으므로 앉는 순간 준비 상태다.
 */
function seat(join: JoinEvent): MatchPlayer {
  return {
    id: join.player.id,
    name: join.player.name,
    kind: join.player.kind,
    bot: join.player.bot,
    connected: true,
    ready: join.player.kind === 'cpu',
    ammo: 0,
    magazines: join.magazines,
    magazinesUsed: 0,
    combo: NO_COMBO,
    maxCombo: NO_COMBO,
    hasPerfect: false,
    spectating: false,
    spectateTarget: null,
    origin: NO_ORIGIN_VISIT,
    raised: false,
    wager: null,
    roundScore: 0,
    totalScore: 0,
  }
}

/**
 * 튜닝에서 방 규칙을 편다. 여럿이에서 `changeCount`는 **인당 예산**이라 `tuning.changesPerPlayer`가 원본이다
 * (`?perPlayer=` - `matchPhases` 머리말). 싱글의 `?changes=`(라운드 총량 12)는 여기 오지 않는다: 여럿이의
 * 총량은 사람 수의 함수이고, 총량을 손잡이로 두면 여섯이 앉은 판에서 인당 두 개가 되는 설정이 가능해진다.
 */
export function roomConfigFrom(tuning: Tuning): MatchConfig['room'] {
  return {
    allowedVerbs: new Set(tuning.allowedVerbs),
    changeCount: tuning.changesPerPlayer,
    // 꾸미기(M10)의 인당 배치 수. 체인저의 '추가'는 이 한도의 대상이 아니다 - `changerVerbContext`가 검사를 끈다.
    placementsPerPlayer: tuning.placementsPerPlayer,
  }
}

/** 기본 설정. 방장이 `configure`로 고친다. 동사별 개수 예고는 기본 켬(`MatchConfig.revealVerbCounts` 주석). */
export function defaultMatchConfig(tuning: Tuning): MatchConfig {
  return {
    room: roomConfigFrom(tuning),
    revealVerbCounts: true,
    humanDecorate: false,
  }
}

/**
 * 매치를 만든다. 방장이 첫 좌석이자 `order[0]`이다. 방은 **여기서 한 번** 세운다 - 라운드가 바뀌어도 방은 같고
 * (9.5절: 방을 외우는 것이 스킬) 변화만 `${seed}:r${round}`로 갈린다.
 *
 * `clockMs`는 0에서 시작한다. 호스트는 첫 사람 이벤트 전에 `tick(atMs)`를 보내야 한다(2.1절) - 로비에는 타이머가
 * 없어 그 첫 틱의 큰 dt는 아무것도 흘리지 않고 시계만 맞춘다.
 */
export function createMatch(
  roomCode: string,
  seed: string,
  host: JoinEvent,
  config: MatchConfig,
  deps: MatchDeps,
): MatchState {
  if (seed.trim() === '') {
    throw new RangeError('createMatch: 시드가 비어 있다 - 재현할 수 없는 방은 만들지 않는다')
  }
  /*
   * 로비의 임시 방. 좌석은 아직 방장 하나뿐이고 여럿이 판의 구획 수는 좌석 수와 같아야 하므로(로드맵 M11),
   * 이 방은 판이 시작되는 순간 굳은 좌석 수로 다시 세워진다(`matchPhases.withRoomForSeats`).
   * 그래도 여기서 한 벌 세워 두는 이유는 `MatchState.original`이 비어 있는 순간을 만들지 않기 위해서다 -
   * 로비 뷰는 방을 싣지 않지만(`matchView.roomVisibility`) 타입에 `null`을 하나 열면 그 `null`을
   * 이후 모든 조각이 다시 걸러야 한다. 고정 평면도로 도는 봇 판·테스트는 여기서 만든 방을 그대로 쓴다.
   */
  const lobbyPlan = deps.planFor(LOBBY_SEAT_COUNT)
  const original = generateBaseRoom(lobbyPlan, seed, deps.catalog, roomOptionsFor(lobbyPlan, deps))
  const player = seat(host)
  return {
    roomCode,
    seed,
    round: LOBBY_ROUND,
    phase: 'lobby',
    clockMs: 0,
    phaseElapsedMs: 0,
    config,
    hostId: player.id,
    order: [player.id],
    players: { [player.id]: player },
    original,
    mutated: original,
    changes: [],
    found: [],
    door: EMPTY_DOOR,
    desks: null,
    predictions: {},
    unfairVotes: {},
    revealIndex: 0,
    skipVotes: [],
    endReason: null,
  }
}

/* --- 좌석 이벤트 (호스트) ----------------------------------------------------------- */

/**
 * 로비에서만 앉을 수 있다. 진행 중 합류는 M6(재접속)의 일이고, 같은 id는 두 번 앉지 않는다.
 * 방장 좌석이 비어 있으면(마지막 사람이 나간 뒤의 빈 로비) 새로 앉는 사람이 방장이다 - `hostId`가 없는 사람을
 * 가리킨 채 남으면 다음에 앉는 사람은 `start`를 영원히 못 보낸다.
 */
function onJoin(state: MatchState, join: JoinEvent): MatchStep {
  if (state.phase !== 'lobby' || playerOf(state, join.player.id) !== undefined) return idle(state)
  const player = seat(join)
  const hostId = playerOf(state, state.hostId) === undefined ? player.id : state.hostId
  return {
    state: {
      ...state,
      hostId,
      order: [...state.order, player.id],
      players: { ...state.players, [player.id]: player },
    },
    effects: [],
  }
}

/**
 * 나간다. 로비에서는 좌석이 사라지고(방장이 나가면 다음 **사람**이 방장 - 이웃은 [시작]을 누를 수 없다. 서버 로비는 방장이 이웃을 먼저
 * 넣는 순서라 `order[0]`이 이웃인 경우가 실제로 있다, M6), 진행 중에는 **좌석이 남고 끊김만 표시**한다 -
 * 끊긴 사람의 발견·점수가 사라지면 남은 사람들의 판이 거짓이 된다(11.3절). M11에서 좌석을 남기는 이유가 하나 더 늘었다:
 * **좌석 수가 곧 평면도**라(`matchSelectors.matchPlan`이 매번 `planFor(좌석 수)`를 부른다) 좌석이 빠지면 판 도중에
 * 구획 수와 벽이 바뀐다.
 *
 * 진행 중에도 방장은 승계한다. `skipPhase`·`nextRound`는 방장만 보낼 수 있고 결과 페이즈는 시간으로 끝나지 않으므로,
 * 방장이 끊긴 채 남으면 매치가 결과 화면에서 영구 정지한다. 접속 중인 사람이 없으면(전원 끊김) 그대로 둔다 -
 * 넘길 사람이 없고, 돌아온 사람이 `reconnect`할 때까지 아무 이벤트도 없다. 승계는 되돌리지 않는다(돌아온 옛 방장은 손님이다).
 *
 * **마지막 미커밋 좌석이 끊기면 바꾸기가 그 자리에서 끝난다.** 끊긴 좌석은 이미 완료로 치므로(`matchDesk.deskDone`)
 * 기다릴 사람이 없는데, 마감을 묻는 자리가 커밋 경로 하나뿐이면 남은 사람들은 빈 화면으로 60초를 다 흘린다 -
 * 뷰(`publicPlayer.changeDone`)는 이미 그 좌석을 완료로 그리므로 화면과 리듀서가 서로 다른 말을 한다.
 * 커밋과 **같은 함수**(`matchPhases.finishChange`)를 지나므로 규칙의 사본이 생기지 않는다.
 *
 * **수색도 같은 이유로 마감을 묻는다.** 끊긴 좌석은 찾는 사람이 아니고(`matchSelectors.isHunting`), 나간 사람이
 * 마지막 술래였거나 남은 것이 전부 남은 술래의 제 구획 것이 되면 그 순간 라운드는 진행이 불가능해진다 -
 * 사격 경로에서만 물으면 아무도 쏘지 않는 판이 5분을 다 흘린다. 판단은 `matchPhases.settleHunt` 하나다.
 */
function onLeave(state: MatchState, id: PlayerId, deps: MatchDeps): MatchStep {
  if (playerOf(state, id) === undefined) return idle(state)
  if (state.phase !== 'lobby') {
    const dropped = patchPlayer(state, id, { connected: false })
    const successor = state.hostId === id ? nextHostCandidate(dropped, id) : null
    const next = successor === null ? dropped : { ...dropped, hostId: successor }
    if (next.phase === 'change' && everyoneCommitted(next)) return finishChange(next, deps)
    if (next.phase === 'hunt') return settleHunt(next)
    return { state: next, effects: [] }
  }

  const order = state.order.filter((seated) => seated !== id)
  const players = { ...state.players }
  delete players[id]
  // 사람이 하나도 안 남으면 나간 사람을 가리킨 채 둔다 - 다음에 앉는 사람이 방장이 된다(`onJoin`).
  const successor = order.find((seated) => players[seated]?.kind === 'human')
  const hostId = state.hostId === id ? (successor ?? state.hostId) : state.hostId
  return { state: { ...state, order, players, hostId }, effects: [] }
}

function onReconnect(state: MatchState, id: PlayerId): MatchStep {
  const player = playerOf(state, id)
  if (player === undefined || player.connected) return idle(state)
  return { state: patchPlayer(state, id, { connected: true }), effects: [] }
}

/* --- 로비 이벤트 (플레이어) --------------------------------------------------------- */

function onReady(state: MatchState, id: PlayerId, ready: boolean): MatchStep {
  const player = playerOf(state, id)
  if (state.phase !== 'lobby' || player === undefined || player.ready === ready) return idle(state)
  return { state: patchPlayer(state, id, { ready }), effects: [] }
}

/**
 * 값을 접어 **라운드를 시작할 수 있는 방 규칙**으로 만든다.
 *
 * 와이어 모양 검사(`net/protocolShapes.isRoomPatchShape`)는 "정수인가 · 원소가 동사인 배열인가"까지만 보고,
 * 값의 뜻은 리듀서가 본다(그 파일 머리말 - 두 층이 같은 검사를 갖지 않게 하는 선). 여기가 그 자리다.
 *
 * 접지 않으면 손으로 만든 `configure` 한 줄이 라운드 시작에서 **던진다** - `changeCount: 0`은 `matchPhases.enterHunt`의
 * "변화가 0개다", 빈 `allowedVerbs`는 `changeGen.generateChanges`의 "허용된 동사가 없다", 20만은 정지에 가까운 루프다.
 * 서버에서 그 던짐은 소켓 핸들러와 `setInterval` 밖으로 나가 프로세스를 내리고 **그 프로세스의 모든 방**을 데려간다(11.3절 -
 * 악의적 클라이언트 한 줄로 호스트가 던지면 안 된다). 로비 UI의 잠금(`ui/lobbySeats.ts`의 스테퍼 범위 · "마지막 하나는 못 끈다")은
 * 이 게이트의 **얼굴**이지 게이트가 아니다 - 버튼이 없는 클라이언트에는 없는 것과 같다.
 *
 * 거절이 아니라 접기인 이유는 `raise.normalizeWager`와 같다 - 부분 패치의 한 필드가 범위 밖이라고 방장의 나머지 조작까지
 * 되돌리면, 화면은 "눌렀는데 아무 일도 없다"가 된다.
 */
function normalizeRoomConfig(next: MatchConfig['room'], previous: MatchConfig['room']): MatchConfig['room'] {
  return {
    // 빈 동사 집합은 무시하고 앞 값을 지킨다 - 이상한 곳을 만들 수 없는 방은 방이 아니다.
    allowedVerbs: next.allowedVerbs.size === 0 ? previous.allowedVerbs : next.allowedVerbs,
    changeCount: clampCount(next.changeCount, CHANGE_COUNT_MIN, CHANGE_COUNT_MAX, previous.changeCount),
    placementsPerPlayer: clampCount(next.placementsPerPlayer, 0, MAX_PLACEMENTS_PER_PLAYER, previous.placementsPerPlayer),
  }
}

/** 정수 구간으로 접는다. 유한수가 아니면 앞 값을 지킨다 - `NaN`을 하한으로 접으면 "0개"가 조용히 "1개"가 된다. */
function clampCount(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback
  return Math.min(max, Math.max(min, Math.trunc(value)))
}

/** 방장만, 로비에서만. `room`은 얕게 덮인다 - 부분 패치로 `changeCount`만 바꾸는 것이 자연스러워야 한다. */
function onConfigure(state: MatchState, id: PlayerId, patch: MatchConfigPatch): MatchStep {
  if (state.phase !== 'lobby' || !isHost(state, id)) return idle(state)
  const config: MatchConfig = {
    ...state.config,
    ...patch,
    room: normalizeRoomConfig({ ...state.config.room, ...patch.room }, state.config.room),
  }
  return { state: { ...state, config }, effects: [] }
}

/** 좌석 전부가 준비됐는가. CPU는 입장 순간부터 준비 상태다. */
export function everyoneReady(state: MatchState): boolean {
  return seatsInOrder(state).every((player) => player.ready)
}

/**
 * 이 사람이 지금 `start`를 누르면 **실제로 시작되는가** - 방장 · 로비 · 전원 준비 · **둘 이상**. `onStart`의 게이트이자
 * `matchView.SelfView.canStart`의 원본이다. 클라이언트(로비의 [시작] 버튼 · 봇 판의 자동 진행)는 이 불리언만 읽고
 * `players.every(ready)`를 다시 세지 않는다 - 조건이 여기 하나 늘어도 버튼의 얼굴이 따라온다.
 *
 * 인원 하한이 규칙인 이유가 M11이다. 혼자 앉은 방은 **라운드가 성립하지 않는다** - 구획이 하나뿐이라 정답표 전부가 내
 * 것이고, 내가 찾을 수 있는 수(`matchView.findableCount`)가 0이라 수색이 시간 종료까지 아무것도 못 찾는 화면이 된다.
 * M10까지는 같은 자리를 `MIN_SEATS_FOR_CHANGER`(<2석이면 시스템이 고친다)가 막았고, 역할이 사라지면서 그 막이 사라졌다.
 * 값은 `core/partyPlan.PARTY_SEATS_MIN`에서 가져온다 - 평면도 표의 하한과 게이트가 갈리면 "시작은 됐는데 구획이 없다"가 된다.
 */
export function canStart(state: MatchState, id: PlayerId): boolean {
  if (seatsInOrder(state).length < PARTY_SEATS_MIN) return false
  return state.phase === 'lobby' && isHost(state, id) && everyoneReady(state)
}

/** 게이트는 `canStart` 하나다. 아니면 참조 동일 - 준비 안 된 사람의 이름은 `matchView`가 보여 준다. */
function onStart(state: MatchState, id: PlayerId, deps: MatchDeps): MatchStep {
  if (!canStart(state, id)) return idle(state)
  return startRound(state, deps)
}

/** 방장만, 결과에서만. "한 판 더"(컨셉시트 8절)가 이 이벤트다. */
function onNextRound(state: MatchState, id: PlayerId, deps: MatchDeps): MatchStep {
  if (state.phase !== 'result' || !isHost(state, id)) return idle(state)
  return startRound(state, deps)
}

/* --- 디스패처 ------------------------------------------------------------------- */

/**
 * 상태 머신 본체. 같은 (상태, 이벤트, deps)는 항상 같은 결과를 낸다.
 * 무시되는 이벤트는 입력 상태를 **그대로 반환**하므로 참조 비교로 "변화 없음"을 알 수 있다.
 */
export function reduceMatch(state: MatchState, ev: MatchEvent, deps: MatchDeps): MatchStep {
  // 넘어가기 표는 **그 페이즈의 것**이다(`skipVotes`). 페이즈가 바뀌면 한 자리에서 지운다 -
  // 전환하는 함수마다 지우면 새 전환이 하나 생길 때마다 지난 페이즈의 표가 따라 들어온다.
  return clearVotesOnPhaseChange(state, reduceMatchEvent(state, ev, deps))
}

function clearVotesOnPhaseChange(before: MatchState, step: MatchStep): MatchStep {
  if (step.state.phase === before.phase || step.state.skipVotes.length === 0) return step
  return { ...step, state: { ...step.state, skipVotes: [] } }
}

function reduceMatchEvent(state: MatchState, ev: MatchEvent, deps: MatchDeps): MatchStep {
  switch (ev.type) {
    case 'tick':
      return onTick(state, ev.atMs, deps)
    case 'join':
      return onJoin(state, ev)
    case 'leave':
      return onLeave(state, ev.playerId, deps)
    case 'reconnect':
      return onReconnect(state, ev.playerId)

    case 'ready':
      return onReady(state, ev.by, ev.ready)
    case 'configure':
      return onConfigure(state, ev.by, ev.patch)
    case 'start':
      return onStart(state, ev.by, deps)
    case 'nextRound':
      return onNextRound(state, ev.by, deps)
    case 'skipPhase':
      return onSkipVote(state, ev.by, deps)

    case 'shoot':
      return onShoot(state, ev.by, ev.hit, deps)
    case 'raise':
      return onRaise(state, ev.by, deps)
    case 'useMagazine':
      return onUseMagazine(state, ev.by, deps)
    case 'spectate':
      return onSpectate(state, ev.by, ev.target)

    case 'requestOrigin':
      return onRequestOrigin(state, ev.by, deps.tuning)
    case 'leaveOrigin':
      return onLeaveOrigin(state, ev.by, deps.tuning)
    case 'leaveQueue':
      return onLeaveQueue(state, ev.by, deps.tuning)

    case 'applyChange':
      return onApplyChange(state, ev.by, ev.request, deps)
    case 'undoChange':
      return onUndoChange(state, ev.by, ev.id, deps)
    case 'commitChanges':
      return onCommitChanges(state, ev.by, ev.wager, deps)

    case 'place':
      return onPlace(state, ev.by, ev.obj, deps)
    case 'unplace':
      return onUnplace(state, ev.by, ev.id, deps)

    // 아래는 타입만 있다. 관전자 참여(M8)가 붙인다.
    case 'predict':
    case 'voteUnfair':
      return idle(state)
  }
}
